import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/auth";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  isImpersonationBackupPresent,
  switchBackToTeacher,
  ImpersonationError,
} from "@/services/impersonationService";

const SwitchBackButton: React.FC = () => {
  const { t } = useLanguage();
  const { profile, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSwitchingBack, setIsSwitchingBack] = useState(false);
  // Presence of a parked teacher session = we got here via "switch to demo".
  // Re-evaluated whenever the identity changes (the same-tab storage write
  // happens right before setSession, whose auth event flips `user`).
  const [canSwitchBack, setCanSwitchBack] = useState<boolean>(() =>
    typeof window !== "undefined" ? isImpersonationBackupPresent() : false
  );

  useEffect(() => {
    setCanSwitchBack(isImpersonationBackupPresent());
  }, [user?.id]);

  useEffect(() => {
    const onStorage = () => setCanSwitchBack(isImpersonationBackupPresent());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleSwitchBack = useCallback(async () => {
    if (isSwitchingBack) return;
    setIsSwitchingBack(true);
    try {
      const { classId } = await switchBackToTeacher();
      toast({
        title: t("Jste zpět jako učitel", "Ste späť ako učiteľ"),
        description: t(
          "Demo účet byl opuštěn, jste přihlášeni jako učitel.",
          "Demo účet bol opustený, ste prihlásení ako učiteľ."
        ),
      });
      navigate(classId ? `/teacher/classes/${classId}` : "/");
    } catch (error) {
      console.error("[SwitchBackButton.handleSwitchBack]", error);
      if (error instanceof ImpersonationError && error.needsLogin) {
        navigate("/auth");
      }
      toast({
        title: t("Návrat se nezdařil", "Návrat sa nezdařil"),
        description:
          error instanceof ImpersonationError
            ? error.message
            : t("Návrat na učitelský účet se nezdařil.", "Návrat na učiteľský účet sa nezdařil."),
        variant: "destructive",
      });
    } finally {
      setIsSwitchingBack(false);
    }
  }, [isSwitchingBack, navigate, t, toast]);

  const isTestStudent = profile?.is_test_student === true;

  if (!isTestStudent) {
    return null;
  }

  // A test student logged in directly (login card on another device, …) has
  // no parked teacher session to return to — keep the previous disabled state.
  if (!canSwitchBack) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled
              className="flex items-center gap-2 bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-900 dark:bg-amber-950 dark:border-amber-700 dark:hover:bg-amber-900 dark:text-amber-200"
            >
              <ArrowLeftRight size={16} />
              <span className="lg:hidden">{t("Zpět na učitele", "Späť na učiteľa")}</span>
              <span className="hidden lg:inline">{t("Přepnout zpět", "Prepnúť späť")}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {t(
                "Přepínání zpět na učitelský účet není dostupné",
                "Prepínanie späť na učiteľský účet nie je dostupné"
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isSwitchingBack}
      onClick={handleSwitchBack}
      title={t("Návrat na váš učitelský účet", "Návrat na váš učiteľský účet")}
      className="flex items-center gap-2 bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-900 dark:bg-amber-950 dark:border-amber-700 dark:hover:bg-amber-900 dark:text-amber-200"
    >
      {isSwitchingBack ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <ArrowLeftRight size={16} />
      )}
      <span className="lg:hidden">{t("Zpět na učitele", "Späť na učiteľa")}</span>
      <span className="hidden lg:inline">{t("Přepnout zpět", "Prepnúť späť")}</span>
    </Button>
  );
};

export default SwitchBackButton;
